/**
 * Firebase Client SDK Configuration
 * Used for client-side authentication and real-time operations
 */

import { initializeApp, getApps, getApp, FirebaseApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, setPersistence, browserLocalPersistence, Auth } from 'firebase/auth';
import { getFirestore, Firestore } from 'firebase/firestore';
import { getFunctions, Functions, httpsCallable } from 'firebase/functions';
import { fetchAndActivate, getRemoteConfig, getValue, RemoteConfig } from 'firebase/remote-config';

/**
 * Firebase Configuration
 * When using Firebase App Hosting with apphosting.yaml's web_app_config,
 * the config is automatically injected as environment variables.
 * This also supports local development with .env.local
 */
const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY || "AIzaSyCq5ysXuyQli6DCuPhb1FrveoxpdUks5_E",
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN || "caniusemyfsa.firebaseapp.com",
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID || "caniusemyfsa",
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET || "caniusemyfsa.firebasestorage.app",
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID || "959450239569",
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID || "1:959450239569:web:68c1ffdf802d2931185e70",
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID || "G-8Z2M60Z570",
  databaseURL: process.env.NEXT_PUBLIC_FIREBASE_DATABASE_URL || "https://caniusemyfsa-default-rtdb.firebaseio.com",
};

// Check if we have a valid Firebase config
const hasFirebaseConfig = !!(firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.apiKey !== 'dummy-api-key-for-build');

// Initialize Firebase (only if we have valid config)
let app: FirebaseApp;
let auth: Auth;
let db: Firestore;
let functions: Functions;
let remoteConfig: RemoteConfig;
let googleProvider: GoogleAuthProvider;

try {
  app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
  auth = getAuth(app);
  db = getFirestore(app);
  functions = getFunctions(app, process.env.NEXT_PUBLIC_FB_REGION || 'us-central1');
  googleProvider = new GoogleAuthProvider();

  // Only initialize Remote Config in the browser
  if (typeof window !== 'undefined') {
    remoteConfig = getRemoteConfig(app);
    
    if (hasFirebaseConfig) {
      // Set auth persistence (only in browser with valid config)
      setPersistence(auth, browserLocalPersistence).catch((error) => {
        console.error('Error setting auth persistence:', error);
      });

      // Configure Remote Config
      remoteConfig.settings.minimumFetchIntervalMillis = 3600000; // 1 hour
      remoteConfig.defaultConfig = {
        // Content
        item_of_the_day: JSON.stringify({
          id: 'default',
          title: 'First Aid Bandages Variety Pack',
          description: 'Essential first aid supplies. Always FSA/HSA eligible without a prescription.',
          image: 'https://images.unsplash.com/photo-1603398938378-e54eab446dde?w=400',
          category: 'First Aid & Medical Supplies',
          tags: ['staff-pick'],
          url: 'https://www.amazon.com/gp/aw/d/B08JH93SHW',
        }),
        staff_picks: JSON.stringify([]),
        lmn_categories: JSON.stringify([
          { name: 'Massage Therapy', icon: '💆', description: 'For chronic pain or injury recovery' },
          { name: 'Acupuncture', icon: '📍', description: 'Alternative pain management' },
          { name: 'Chiropractic Care', icon: '🦴', description: 'Spinal and musculoskeletal treatment' },
          { name: 'Exercise Equipment', icon: '🏋️', description: 'Physical therapy and rehabilitation' },
          { name: 'Air Purifiers', icon: '💨', description: 'Respiratory conditions and allergies' },
          { name: 'Special Diets', icon: '🥗', description: 'Medical conditions requiring specific nutrition' },
          { name: 'Weight Loss Programs', icon: '⚖️', description: 'Obesity treatment prescribed by doctor' },
          { name: 'Service Animals', icon: '🐕', description: 'Trained animals for medical conditions' },
        ]),
        trending_searches: JSON.stringify([
          { term: 'Bandages', icon: '🩹' },
          { term: 'Sunscreen', icon: '☀️' },
          { term: 'Contact Lenses', icon: '👁️' },
          { term: 'Thermometer', icon: '🌡️' },
          { term: 'Pain Relievers', icon: '💊' },
          { term: 'First Aid Kit', icon: '🏥' },
          { term: 'Blood Pressure Monitor', icon: '❤️' },
          { term: 'Heating Pad', icon: '🔥' },
          { term: 'Ice Pack', icon: '🧊' },
          { term: 'Reading Glasses', icon: '👓' },
        ]),
        // Feature Flags
        feature_barcode_scanner: 'true',
        feature_url_detection: 'true',
        // Limits
        rate_limit_anonymous: '10',
        rate_limit_authenticated: '100',
        cache_ttl_hours: '24',
        // Maintenance
        maintenance_mode: 'false',
        maintenance_message: 'We\'re currently performing scheduled maintenance. Please check back soon!',
      };

      // Fetch and activate remote config
      fetchAndActivate(remoteConfig).catch((error) => {
        console.error('Error fetching remote config:', error);
      });

      // Google Auth Provider
      googleProvider.setCustomParameters({
        prompt: 'select_account',
      });
    }
  } else {
    // Create dummy Remote Config for build time
    remoteConfig = {
      defaultConfig: {
        item_of_the_day: JSON.stringify({ id: 'default', title: 'Loading...', description: '', image: '', category: '', tags: [], url: '#' }),
        staff_picks: JSON.stringify([]),
        lmn_categories: JSON.stringify([]),
        trending_searches: JSON.stringify([]),
        feature_barcode_scanner: 'true',
        feature_url_detection: 'true',
        rate_limit_anonymous: '10',
        rate_limit_authenticated: '100',
        cache_ttl_hours: '24',
        maintenance_mode: 'false',
        maintenance_message: '',
      },
    } as unknown as RemoteConfig;
  }
} catch (error) {
  console.warn('Firebase initialization skipped (likely during build):', error);
  // Create dummy instances for build time
  app = {} as FirebaseApp;
  auth = {} as Auth;
  db = {} as Firestore;
  functions = {} as Functions;
  remoteConfig = {
    defaultConfig: {
      item_of_the_day: JSON.stringify({ id: 'default', title: 'Loading...', description: '', image: '', category: '', tags: [], url: '#' }),
      staff_picks: JSON.stringify([]),
      lmn_categories: JSON.stringify([]),
      trending_searches: JSON.stringify([]),
      feature_barcode_scanner: 'true',
      feature_url_detection: 'true',
      rate_limit_anonymous: '10',
      rate_limit_authenticated: '100',
      cache_ttl_hours: '24',
      maintenance_mode: 'false',
      maintenance_message: '',
    },
  } as unknown as RemoteConfig;
  googleProvider = {} as GoogleAuthProvider;
}

// Helper function to get Remote Config value
export const getRemoteConfigValue = (key: string): string => {
  try {
    // Only use getValue if we're in the browser and remoteConfig is properly initialized
    if (typeof window !== 'undefined' && remoteConfig && 'app' in remoteConfig) {
      return getValue(remoteConfig, key).asString();
    }
    // Fall back to default config
    return remoteConfig?.defaultConfig?.[key] as string || '';
  } catch (error) {
    console.error(`Error getting remote config value for ${key}:`, error);
    return remoteConfig?.defaultConfig?.[key] as string || '';
  }
};

export { app, auth, db, functions, remoteConfig, googleProvider, httpsCallable };

